import {
  alpha,
  Card,
  CardContent,
  Checkbox,
  Grid,
  Radio,
  Typography,
} from '@mui/material';
import type { WizardPrimalCut } from '../types';
import { useMemo } from 'react';

export type CutCardInputType = 'checkbox' | 'radio';

export interface CutCardProps {
  cut: WizardPrimalCut;
  inputType?: CutCardInputType;
  value?: string;
  checked?: boolean;
  disabled?: boolean;
  blockedByText?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export function CutCard({
  cut,
  inputType = 'checkbox',
  value,
  checked,
  disabled: propDisabled,
  blockedByText,
  onChange,
}: CutCardProps) {
  const InputEl = inputType === 'radio' ? Radio : Checkbox;

  const disabled = useMemo(
    () => propDisabled && !checked,
    [propDisabled, checked],
  );

  const handleCardClick = () => {
    if (disabled || !onChange) return;

    const syntheticEvent = {
      target: {
        name: cut.name,
        value: value || cut._id,
        checked: !checked,
      },
    } as React.ChangeEvent<HTMLInputElement>;

    onChange(syntheticEvent);
  };

  return (
    <Card
      data-testid="cut-card"
      onClick={handleCardClick}
      sx={{
        borderRadius: '9px',
        my: 2,
        width: '100%',
        border: '2px solid', // Always 2px border to prevent layout shift
        borderColor: checked ? 'primary.main' : 'divider', // Divider color when not selected

        // selected state
        ...(checked
          ? {
              backgroundColor: (theme) =>
                alpha(theme.palette.primary.main, 0.1),
              // improve readability on colored background
              '& .cutcard-title': {
                color: 'primary.contrastText',
              },
            }
          : {}),

        // hover (only when not disabled)
        ...(disabled
          ? { opacity: 0.7, cursor: 'not-allowed' }
          : {
              cursor: 'pointer',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: (theme) =>
                  checked
                    ? alpha(theme.palette.primary.main, 0.1)
                    : alpha(theme.palette.primary.main, 0.05),
              },
            }),

        // Ensure all child elements use pointer cursor when not disabled
        ...(!disabled && {
          '& *': {
            cursor: 'pointer !important',
          },
        }),
      }}
      variant="outlined"
    >
      <CardContent sx={{ '&:last-child': { pb: 2 } }}>
        <Grid container spacing={1} alignItems="center" wrap="nowrap">
          <Grid sx={{ display: 'flex', alignItems: 'center' }}>
            <InputEl
              name={cut.name}
              value={value}
              checked={checked}
              disabled={disabled}
              onChange={onChange}
              size="medium"
            />
          </Grid>
          <Grid sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="subtitle1"
              sx={{
                color: 'text.primary',
                fontSize: '1.125rem',
                fontWeight: '500',
                lineHeight: '1.25rem',
                mb: cut.description || blockedByText ? 0.5 : 0,
              }}
            >
              {cut.name}
            </Typography>
            {blockedByText && (
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.875rem',
                  lineHeight: '1.25rem',
                }}
              >
                {blockedByText}
              </Typography>
            )}
            {(cut as any).description && (
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.875rem',
                  lineHeight: '1.25rem',
                }}
              >
                {cut.description}
              </Typography>
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
